import { Dialog, useDialogManager } from '../../../widgets/dialog';
import { showErrorNotification } from '../../../widgets/notificationToaster';
import React, { useEffect, useRef, useState } from 'react';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import UploadProgressRow from 'src/components/widgets/uploadProgressRow';
import { T } from 'src/shared/helpers';

enum TaskStatus {
    uploading,
    progress,
    checking,
    success,
    completed_success,
    completed_error
}

type Task = {
    status: TaskStatus;
    uploadProgress?: number;
    meshes: BABYLON.AbstractMesh[];
};

export const UploadDialog = ({
    files,
    damProductAssetUrl,
    onComplete
}: {
    files?: FileList;
    damProductAssetUrl?: string;
    onComplete: (meshes: BABYLON.AbstractMesh[]) => void;
}) => {
    const [sceneEditor] = usePreview3DStore((x) => [x.sceneEditor]);
    const [progress, setProgress] = useState(0);
    const { currentDialogId, closeDialog } = useDialogManager();

    const currentTaskRef = useRef<Task | null | undefined>(null);

    useEffect(() => {
        sceneEditor?.loadSceneSourceFiles(
            files ? Array.from(files) : undefined,
            damProductAssetUrl ? damProductAssetUrl : undefined,
            () => null,
            () => {
                showErrorNotification({
                    message: T._('Failed to upload the files.', 'Admin')
                });

                closeDialog(currentDialogId);
            }
        );
    }, []);

    useEffect(() => {
        sceneEditor?.addSceneSourceTaskCheckListener(handleSceneTaskCheck);

        return () => sceneEditor?.removeSceneSourceTaskCheckListener(handleSceneTaskCheck);
    }, []);

    const handleSceneTaskCheck = async () => {
        const task = sceneEditor?.sceneSourceTasks[0];

        if (task) currentTaskRef.current = task as unknown as Task; // I can't import them from the lib because it requires the JS to be loaded first

        if (currentTaskRef.current) {
            if (currentTaskRef.current.status === TaskStatus.uploading) setProgress(currentTaskRef.current.uploadProgress!);

            if (currentTaskRef.current.status === TaskStatus.progress) {
                setProgress(100);
            }

            if (currentTaskRef.current.status == TaskStatus.completed_error) {
                showErrorNotification({
                    message: T._('Failed to upload the files.', 'Admin')
                });

                closeDialog(currentDialogId);
                currentTaskRef.current = undefined;
                return;
            }

            if (currentTaskRef.current && !task) {
                onComplete(currentTaskRef.current.meshes);

                currentTaskRef.current = undefined;
            }
        }
    };

    return (
        <Dialog>
            <UploadProgressRow label={T._('Uploading...', 'Admin')} status='UPLOADING' progress={progress} />
        </Dialog>
    );
};
